// The ONLY place fetch lives (ADR 0003 §4). Components and pages never call fetch; they call a
// typed function from src/api/ through TanStack Query. A refusal from the API is a typed value,
// not a thrown string, so the UI can say why rather than "something went wrong".

import { orgSelection } from '../hooks/useOrg'

export interface Refusal {
  reason: string
  message: string
  details: Record<string, unknown>
  auditId: number
}

export class RefusedError extends Error {
  constructor(public readonly refusal: Refusal, public readonly status: number) {
    super(`${refusal.reason}: ${refusal.message}`)
    this.name = 'RefusedError'
  }
}

const base = (import.meta.env.VITE_SCOUT_API ?? '').replace(/\/$/, '')
const TOKEN_KEY = 'scout.session'

/** The bearer token for this browser. Kept here so every call carries it and nothing else knows. */
export const session = {
  get(): string | null {
    return window.localStorage.getItem(TOKEN_KEY)
  },
  set(token: string | null) {
    if (token) window.localStorage.setItem(TOKEN_KEY, token)
    else window.localStorage.removeItem(TOKEN_KEY)
  },
}

/** Fires when the API says the session is gone, so the shell can send the person to sign in. */
export const onUnauthenticated: { handler: (() => void) | null } = { handler: null }

/** The analytics hook (lib/telemetry): every call's method, path, status, time and a preview of the answer. */
export const onCall: { record: ((method: string, path: string, status: number, durationMs: number, requestBytes: number, response: string | null, error?: string) => void) | null } = { record: null }

type Params = Record<string, string | number | undefined>

async function send<T>(method: string, path: string, body?: unknown, params?: Params): Promise<T> {
  const url = new URL(base + path, window.location.origin)
  // Which org you are looking at rides on every GET (mine · all · a key). Only on GETs: a write is
  // always to the thing it names, never to "whatever is selected". A caller that passes org itself
  // wins — passing undefined is how a call says the question has no org (the API ignores it anyway
  // on owner-only surfaces: your mailbox, your voice, your drafts).
  const query: Params = method === 'GET' ? { org: orgSelection(), ...params } : params ?? {}
  for (const [k, v] of Object.entries(query)) if (v !== undefined) url.searchParams.set(k, String(v))

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = session.get()
  if (token) headers.Authorization = `Bearer ${token}`

  const payload = body === undefined ? undefined : JSON.stringify(body)
  const started = performance.now()
  let res: Response
  try { res = await fetch(url.toString(), { method, headers, body: payload }) }
  catch (e) { onCall.record?.(method, path, 0, performance.now() - started, payload?.length ?? 0, null, e instanceof Error ? e.message : String(e)); throw e }
  const text = res.status === 204 ? '' : await res.text()
  onCall.record?.(method, path, res.status, performance.now() - started, payload?.length ?? 0, text, res.ok ? undefined : `HTTP ${res.status}`)
  if (res.status === 204) return undefined as T
  if (res.ok) return JSON.parse(text) as T

  let refusal: Refusal | null = null
  try { refusal = JSON.parse(text) as Refusal } catch { refusal = null }
  if (refusal && typeof refusal.reason === 'string') {
    if (refusal.reason === 'Unauthenticated' && !path.startsWith('/api/auth/login')) onUnauthenticated.handler?.()
    throw new RefusedError(refusal, res.status)
  }
  throw new Error(`HTTP ${res.status} from ${path}`)
}

export const getJson = <T>(path: string, params?: Params) => send<T>('GET', path, undefined, params)
export const postJson = <T>(path: string, body?: unknown) => send<T>('POST', path, body ?? {})
export const putJson = <T>(path: string, body?: unknown) => send<T>('PUT', path, body ?? {})
export const patchJson = <T>(path: string, body?: unknown) => send<T>('PATCH', path, body ?? {})
export const deleteJson = <T>(path: string) => send<T>('DELETE', path)

/** An API path as the browser should load it (an <img src>, a download): the same base every call uses. */
export const apiUrl = (path: string) => (path.startsWith('/') ? base + path : path)

/** Multipart upload with progress. Same refusal contract as send(). */
export function uploadForm<T>(path: string, form: FormData, onProgress?: (fraction: number) => void, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, base + path)
    xhr.setRequestHeader('Accept', 'application/json')
    const token = session.get()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total) }
    xhr.onerror = () => reject(new Error(`Network error uploading to ${path}`))
    xhr.onload = () => {
      let body: unknown = null
      try { body = JSON.parse(xhr.responseText) } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body as T)
      const refusal = body as Refusal | null
      if (refusal && typeof refusal.reason === 'string') {
        if (refusal.reason === 'Unauthenticated') onUnauthenticated.handler?.()
        return reject(new RefusedError(refusal, xhr.status))
      }
      reject(new Error(`HTTP ${xhr.status} from ${path}`))
    }
    xhr.send(form)
  })
}
